<script setup>
/* eslint-disable vue/no-bare-strings-in-template */
import { formatTime } from '@chatwoot/utils';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import ReportLineChart from '../ReportLineChart.vue';
import ReportCard from '../ReportCard.vue';

const props = defineProps({
  accountSummary: {
    type: Object,
    default: () => ({}),
  },
  accountReport: {
    type: Object,
    default: () => ({}),
  },
  isSummaryLoading: {
    type: Boolean,
    default: false,
  },
  selectedMetric: {
    type: String,
    default: '',
  },
});

defineEmits(['metricSelect']);

const metricDefinitions = [
  {
    key: 'conversations_count',
    label: 'Conversas',
    icon: 'chat',
    accent: 'blue',
    supportsConversationList: true,
  },
  {
    key: 'outgoing_messages_count',
    label: 'Mensagens enviadas',
    icon: 'send',
    accent: 'cyan',
    supportsConversationList: true,
  },
  {
    key: 'avg_first_response_time',
    label: 'Tempo de primeira resposta',
    icon: 'calendar-clock',
    accent: 'violet',
    isTime: true,
    isInverse: true,
  },
  {
    key: 'avg_resolution_time',
    label: 'Tempo de resolução',
    icon: 'document-list-clock',
    accent: 'pink',
    isTime: true,
    isInverse: true,
  },
  {
    key: 'resolutions_count',
    label: 'Contagem de resolução',
    icon: 'checkmark-circle',
    accent: 'green',
    supportsConversationList: true,
  },
  {
    key: 'reply_time',
    label: 'Tempo de espera do cliente',
    icon: 'status',
    accent: 'blue',
    isTime: true,
    isInverse: true,
  },
];

const accentClass = accent => {
  const accents = {
    blue: 'text-woot-500 bg-woot-50 dark:bg-woot-900/40',
    cyan: 'text-cyan-500 bg-cyan-50 dark:bg-cyan-900/40',
    violet: 'text-violet-500 bg-violet-50 dark:bg-violet-900/40',
    pink: 'text-pink-500 bg-pink-50 dark:bg-pink-900/40',
    green: 'text-green-500 bg-green-50 dark:bg-green-900/40',
  };
  return accents[accent] || accents.blue;
};

const strokeClass = accent => {
  const accents = {
    blue: 'stroke-woot-500',
    cyan: 'stroke-cyan-500',
    violet: 'stroke-violet-500',
    pink: 'stroke-pink-500',
    green: 'stroke-green-500',
  };
  return accents[accent] || accents.blue;
};

const accentColor = accent => {
  const accents = {
    blue: '#1f93ff',
    cyan: '#06b6d4',
    violet: '#8b5cf6',
    pink: '#ec4899',
    green: '#22c55e',
  };
  return accents[accent] || accents.blue;
};

const metricValue = metric => {
  const value = props.accountSummary[metric.key] || 0;
  return metric.isTime ? formatTime(value) : Number(value).toLocaleString();
};

const metricTrend = metric => {
  const previous = Number(props.accountSummary.previous?.[metric.key] || 0);
  if (!previous) return 0;

  const current = Number(props.accountSummary[metric.key] || 0);
  return Math.round(((current - previous) / previous) * 100);
};

const trendClass = metric => {
  const trend = metricTrend(metric);
  const isGood = metric.isInverse ? trend <= 0 : trend >= 0;
  return isGood
    ? 'text-green-600 dark:text-green-400'
    : 'text-red-500 dark:text-red-400';
};

const metricSeries = metric =>
  (props.accountReport.data?.[metric.key] || []).map(item =>
    Number(item.value ?? item.count ?? 0)
  );

const sparklinePoints = metric => {
  const values = metricSeries(metric);
  if (!values.length) return '';

  const max = Math.max(...values, 1);
  const min = Math.min(...values);
  const range = max - min || 1;
  const lastIndex = Math.max(values.length - 1, 1);

  return values
    .map((value, index) => {
      const x = (index / lastIndex) * 100;
      const y = 28 - ((value - min) / range) * 22;
      return `${x},${y}`;
    })
    .join(' ');
};

const metricChartData = metric => {
  const data = props.accountReport.data?.[metric.key] || [];
  const color = accentColor(metric.accent);

  return {
    labels: data.map(item => format(fromUnixTime(item.timestamp), 'dd MMM')),
    datasets: [
      {
        label: metric.label,
        data: metricSeries(metric),
        borderColor: color,
        backgroundColor: color,
      },
    ],
  };
};

const metricChartOptions = metric => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: context =>
          metric.isTime
            ? formatTime(context.parsed.y)
            : Number(context.parsed.y).toLocaleString(),
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { maxTicksLimit: 4 },
    },
    y: {
      beginAtZero: true,
      ticks: {
        callback: value =>
          metric.isTime ? formatTime(value) : Number(value).toLocaleString(),
      },
    },
  },
});

const isLoading = metric =>
  props.isSummaryLoading ||
  Boolean(props.accountReport.isFetching?.[metric.key]);
</script>

<template>
  <div class="space-y-4">
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      <ReportCard
        v-for="metric in metricDefinitions"
        :key="metric.key"
        :is-loading="isLoading(metric)"
        :selectable="metric.supportsConversationList"
        :selected="metric.key === selectedMetric"
        @select="$emit('metricSelect', metric)"
      >
        <div class="flex items-start gap-4">
          <span class="agent-icon" :class="accentClass(metric.accent)">
            <fluent-icon :icon="metric.icon" size="24" />
          </span>
          <div class="min-w-0">
            <p class="mb-1 text-sm text-slate-600 dark:text-slate-300">
              {{ metric.label }}
            </p>
            <strong class="text-3xl text-slate-900 dark:text-slate-50">
              {{ metricValue(metric) }}
            </strong>
            <span
              v-if="metricTrend(metric)"
              class="block mt-1 text-sm font-medium"
              :class="trendClass(metric)"
            >
              {{ metricTrend(metric) > 0 ? '+' : '' }}{{ metricTrend(metric) }}%
            </span>
          </div>
        </div>
        <div v-if="metricSeries(metric).length" class="h-48 mt-5">
          <ReportLineChart
            :collection="metricChartData(metric)"
            :chart-options="metricChartOptions(metric)"
          />
        </div>
        <p
          v-else
          class="flex items-center justify-center h-48 mt-5 text-sm text-slate-500 dark:text-slate-400"
        >
          Sem dados suficientes
        </p>
      </ReportCard>
    </div>

    <ReportCard>
      <div class="flex items-center gap-3 mb-5">
        <span class="agent-icon text-woot-500 bg-woot-50 dark:bg-woot-900/40">
          <fluent-icon icon="arrow-trending-lines" size="24" />
        </span>
        <div>
          <h3
            class="mb-0 text-lg font-medium text-slate-900 dark:text-slate-50"
          >
            Resumo de desempenho
          </h3>
          <p class="mb-0 text-sm text-slate-500 dark:text-slate-400">
            Comparativo das principais métricas no período selecionado.
          </p>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        <div
          v-for="metric in metrics"
          :key="`summary-${metric.key}`"
          class="px-3 text-center border-r border-solid last:border-r-0 border-slate-75 dark:border-slate-700"
        >
          <span
            class="inline-flex agent-icon"
            :class="accentClass(metric.accent)"
          >
            <fluent-icon :icon="metric.icon" size="20" />
          </span>
          <svg
            v-if="sparklinePoints(metric)"
            class="w-full h-12 mt-3"
            viewBox="0 0 100 32"
            preserveAspectRatio="none"
          >
            <polyline
              :points="sparklinePoints(metric)"
              class="fill-none"
              :class="strokeClass(metric.accent)"
              stroke-width="2"
              vector-effect="non-scaling-stroke"
            />
          </svg>
          <strong
            class="block mt-2 text-base text-slate-900 dark:text-slate-50"
          >
            {{ metricValue(metric) }}
          </strong>
          <span class="block mt-1 text-xs text-slate-500 dark:text-slate-400">
            {{ metric.label }}
          </span>
        </div>
      </div>
    </ReportCard>
  </div>
</template>

<style lang="scss" scoped>
.agent-icon {
  @apply flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-full;
}
</style>
